import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { AGENT_ICONS, type AgentWizardData } from '@/schemas/agent-wizard'
import { Check } from 'lucide-react'
import { ChaveDeAtendimento } from './chave-de-atendimento'
import type { AgenteDeAtendimento } from '@/schemas/painel'

interface Props {
  organizationId?: string | null
  agentId?: string
  atendimento?: AgenteDeAtendimento | null
  aoMudarAtendimento?: () => void | Promise<void>
}

export function StepBasicInfo({
  organizationId,
  agentId,
  atendimento = null,
  aoMudarAtendimento,
}: Props = {}) {
  const { t } = useTranslation('agents')
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AgentWizardData>()

  const selectedIcon = watch('icon')

  return (
    <div className="space-y-6">
      {/* Icon Selection */}
      <div className="space-y-3">
        <Label className="text-gray-300">{t('wizard.basicInfo.iconLabel')}</Label>
        {/* **OITO COLUNAS FIXAS COM BOTÃO DE LARGURA FIXA SE SOBREPUNHAM.**
            `grid-cols-8` divide o espaço disponível, mas `h-12 w-12` ignora a
            trilha: oito botões de 48px mais sete vãos pedem 440px, e o painel
            do wizard oferece 285px no mobile e 338px no desktop (o resto é
            barra lateral e pré-visualização). Item de grade que não cabe na
            trilha transborda por cima do vizinho em vez de reduzir — medidos
            21 pares sobrepostos, até 14px de invasão. Agora a grade escolhe
            quantas colunas cabem e o botão acompanha a trilha. */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(2.75rem,1fr))] gap-2">
          {AGENT_ICONS.map((icon) => (
            <button
              key={icon}
              type="button"
              onClick={() => setValue('icon', icon)}
              className={cn(
                // `scale-110` no selecionado crescia para dentro do vizinho,
                // que é o mesmo defeito em miniatura. O anel marca a escolha
                // sem ocupar espaço de ninguém.
                'relative flex aspect-square w-full items-center justify-center rounded-xl text-2xl transition-all duration-200',
                selectedIcon === icon
                  ? 'bg-indigo-500/30 border-2 border-indigo-500 ring-2 ring-indigo-500/30 shadow-lg shadow-indigo-500/20'
                  : 'bg-white/5 border border-gray-700/50 hover:bg-white/10 hover:border-gray-600'
              )}
            >
              {icon}
              {selectedIcon === icon && (
                <div className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500 text-white">
                  <Check className="h-3 w-3" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Name Input */}
      <div className="space-y-2">
        <Label htmlFor="name" className="text-gray-300">
          {t('wizard.basicInfo.nameRequired')} <span className="text-red-400">*</span>
        </Label>
        <Input
          id="name"
          {...register('name')}
          placeholder={t('wizard.basicInfo.namePlaceholder')}
          className={cn(
            'bg-white/5 border-gray-700/50 text-white placeholder:text-gray-500',
            'focus:border-indigo-500 focus:ring-indigo-500/20',
            errors.name && 'border-red-500 focus:border-red-500'
          )}
        />
        {errors.name && (
          <p className="text-sm text-red-400">{errors.name.message}</p>
        )}
        <p className="text-xs text-gray-500">
          {t('wizard.basicInfo.nameHelper')}
        </p>
      </div>

      {/* Description Textarea */}
      <div className="space-y-2">
        <Label htmlFor="description" className="text-gray-300">
          {t('wizard.basicInfo.description')}
        </Label>
        <textarea
          id="description"
          {...register('description')}
          placeholder={t('wizard.basicInfo.descriptionPlaceholder')}
          rows={4}
          className={cn(
            'w-full rounded-xl px-4 py-3 text-sm resize-none',
            'bg-white/5 border border-gray-700/50 text-white placeholder:text-gray-500',
            'focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20 focus:outline-none',
            'transition-colors duration-200',
            errors.description && 'border-red-500 focus:border-red-500'
          )}
        />
        {errors.description && (
          <p className="text-sm text-red-400">{errors.description.message}</p>
        )}
        <p className="text-xs text-gray-500">
          {t('wizard.basicInfo.descriptionHelper')}
        </p>
      </div>

      {/* A chave que decide quem atende — spec 096 */}
      <ChaveDeAtendimento
        organizationId={organizationId}
        agentId={agentId}
        atendimento={atendimento}
        aoMudar={() => aoMudarAtendimento?.()}
      />

      {/* Preview Card */}
      <div className="mt-8 p-4 rounded-xl bg-white/5 border border-gray-700/50">
        <p className="text-xs text-gray-500 mb-3">{t('wizard.basicInfo.previewCard')}</p>
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-indigo-500/20 text-3xl">
            {selectedIcon || '🤖'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate">
              {watch('name') || t('wizard.basicInfo.defaultName')}
            </p>
            <p className="text-sm text-gray-400 truncate">
              {watch('description') || t('wizard.basicInfo.defaultDescription')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
